/**
 * Manual journals: the draft changes (create, save, delete, post), the
 * draft's files, reversing a posted entry and the lock exception a refused
 * posting asks for. Every change re-reads the company's books, refused or not:
 * a stale save must show what the other person saved.
 */
/* eslint-disable @linkhr/linkhr/no-raw-tanstack-query -- the accounting app keeps its own hooks (it may not import the HR ones); keys come from qk */
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import {
  ApiError,
  putToPresignedUrl,
  qk,
  readStepUpChallenge,
  useApiClient,
  type components,
} from "@linkhr/api-client/core";
import { useAuth } from "@linkhr/auth/react";
import type { Draft, Entry, InventoryCountResult, LockException } from "./ledger";
import { COMPANY, answer, type Untyped } from "./untyped";

type S = components["schemas"];
export type InventoryCountRequest = S["AcctInventoryCountRequest"];
type DraftRequest = S["AcctDraftRequest"];
type PresignAnswer = S["AcctPresignAnswer"];
type FileUrl = S["AcctFileUrl"];
type ExceptionRequest = S["AcctOpenExceptionRequest"];

/**
 * A change to the books: every answer, refused or not, re-reads the company's
 * ledger reads. A step-up demand is not one: nothing changed, the call comes again.
 */
export function useLedgerChange<TVars, TData>(
  companyId: string,
  send: (client: Untyped, vars: TVars) => Promise<TData>,
): UseMutationResult<TData, ApiError, TVars> {
  const client = useApiClient() as unknown as Untyped;
  const queryClient = useQueryClient();
  const { tenantId } = useAuth();
  return useMutation<TData, ApiError, TVars>({
    mutationFn: (vars) => send(client, vars),
    onSettled: (_data, error) =>
      readStepUpChallenge(error) !== null
        ? undefined
        : queryClient.invalidateQueries({
            queryKey: qk.accounting.ledger(tenantId ?? "", companyId),
          }),
  });
}

const DRAFTS = `${COMPANY}/journal-entries`;
const DRAFT = `${DRAFTS}/{draftId}`;

export const useCreateDraft = (
  companyId: string,
): UseMutationResult<Draft, ApiError, DraftRequest> =>
  useLedgerChange(companyId, (client, body) =>
    answer<Draft>(client.POST(DRAFTS, { params: { path: { companyId } }, body })),
  );

/** `If-Match` carries the version read; a stale one answers 409 ACCT_DRAFT_STALE. */
export const useSaveDraft = (
  companyId: string,
): UseMutationResult<Draft, ApiError, { draftId: string; version: number; body: DraftRequest }> =>
  useLedgerChange(companyId, (client, { draftId, version, body }) =>
    answer<Draft>(
      client.PUT(DRAFT, {
        params: { path: { companyId, draftId } },
        body,
        headers: { "If-Match": String(version) },
      }),
    ),
  );

export function useDeleteDraft(
  companyId: string,
): UseMutationResult<void, ApiError, { draftId: string }> {
  const queryClient = useQueryClient();
  const { tenantId } = useAuth();
  return useLedgerChange(companyId, async (client, { draftId }) => {
    await answer<void>(client.DELETE(DRAFT, { params: { path: { companyId, draftId } } }));
    // Gone: drop its read, so the refresh that follows doesn't ask for it (a 404).
    queryClient.removeQueries({
      queryKey: qk.accounting.ledgerRead(tenantId ?? "", companyId, "draft", { draftId }),
      exact: true,
    });
  });
}

/** Posts the draft as last saved; posting a posted draft answers it again. */
export const usePostDraft = (
  companyId: string,
): UseMutationResult<Draft, ApiError, { draftId: string }> =>
  useLedgerChange(companyId, (client, { draftId }) =>
    answer<Draft>(client.POST(`${DRAFT}:post`, { params: { path: { companyId, draftId } } })),
  );

/**
 * A file onto a saved draft: presign, PUT the bytes (no credentials), then
 * attach. A file over the presign's `maxBytes` stops before the upload, as the
 * server's 413 would.
 */
export const useAttachFile = (
  companyId: string,
): UseMutationResult<Draft, ApiError, { draftId: string; file: File }> =>
  useLedgerChange(companyId, async (client, { draftId, file }) => {
    const contentType = file.type !== "" ? file.type : "application/octet-stream";
    const presign = await answer<PresignAnswer>(
      client.POST(`${DRAFTS}/attachments:presign`, {
        params: { path: { companyId } },
        body: { contentType, fileName: file.name },
      }),
    );
    if (file.size > presign.maxBytes) throw uploadError("FILE_TOO_LARGE", 413);
    const put = await putToPresignedUrl(presign.uploadUrl, file, { contentType });
    if (!put.ok) throw uploadError("INTERNAL_ERROR", put.status);
    return answer<Draft>(
      client.POST(`${DRAFT}/attachments`, {
        params: { path: { companyId, draftId } },
        body: { storageKey: presign.storageKey },
      }),
    );
  });

/** The storage PUT is not an API call: its failure is worded like the API's own. */
export function uploadError(
  errorCode: "FILE_TOO_LARGE" | "INTERNAL_ERROR",
  status: number,
): ApiError {
  return new ApiError({ errorCode, status, title: "", correlationId: "" });
}

export const useDetachFile = (
  companyId: string,
): UseMutationResult<Draft, ApiError, { draftId: string; attachmentId: string }> =>
  useLedgerChange(companyId, (client, { draftId, attachmentId }) =>
    answer<Draft>(
      client.DELETE(`${DRAFT}/attachments/{attachmentId}`, {
        params: { path: { companyId, draftId, attachmentId } },
      }),
    ),
  );

/** A short-lived download URL for one of a draft's files (read on demand, never cached). */
export function useFileUrl(
  companyId: string,
): (draftId: string, attachmentId: string) => Promise<string> {
  const client = useApiClient() as unknown as Untyped;
  return async (draftId, attachmentId) =>
    (
      await answer<FileUrl>(
        client.GET(`${DRAFT}/attachments/{attachmentId}`, {
          params: { path: { companyId, draftId, attachmentId } },
        }),
      )
    ).url;
}

/** Answers the reversal entry. */
export const useReverseEntry = (
  companyId: string,
): UseMutationResult<
  Entry,
  ApiError,
  { entryId: string; accountingDate: string; reason: string }
> =>
  useLedgerChange(companyId, (client, { entryId, accountingDate, reason }) =>
    answer<Entry>(
      client.POST(`${COMPANY}/entries/{entryId}:reverse`, {
        params: { path: { companyId, entryId } },
        body: { accountingDate, ...(reason !== "" ? { reason } : {}) },
      }),
    ),
  );

/**
 * Books a stock count's difference. `key` is the `Idempotency-Key`: one per post action, the
 * same (with the same body) on its retry, which answers the count already booked (200).
 */
export const useCreateInventoryCount = (
  companyId: string,
): UseMutationResult<
  InventoryCountResult,
  ApiError,
  { key: string; body: InventoryCountRequest }
> =>
  useLedgerChange(companyId, (client, { key, body }) =>
    answer<InventoryCountResult>(
      client.POST(`${COMPANY}/inventory-counts`, {
        params: { path: { companyId } },
        body,
        headers: { "Idempotency-Key": key },
      }),
    ),
  );

/** For the caller, with the server's default expiry. */
export const useOpenLockException = (
  companyId: string,
): UseMutationResult<
  LockException,
  ApiError,
  { periodId: string; operation: ExceptionRequest["operation"]; reason: string }
> =>
  useLedgerChange(companyId, (client, { periodId, operation, reason }) =>
    answer<LockException>(
      client.POST(`${COMPANY}/periods/{periodId}/lock-exceptions`, {
        params: { path: { companyId, periodId } },
        body: { operation, reason },
      }),
    ),
  );
